import { DestroyRef, Directive, EmbeddedViewRef, inject, Input, OnInit, TemplateRef, ViewContainerRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LoggedUserService } from '@improver/infrastructure/auth-data-layer';
import { EmploymentAccessService } from './../services/employment-access.service';
import { EmploymentAccessPermission } from '@improver/shared/permissions';
import { castArray } from '@improver/shared/utils';
import { map, some, toNumber } from 'lodash-es';
import { merge, Observable } from 'rxjs';
import { first } from 'rxjs/operators';
import { EmploymentAccess } from '../employment-access.model';
import { EmploymentAccessDataFetcherService } from '../services/employment-access-data-fetcher.service';

@Directive({
  // eslint-disable-next-line:directive-selector
  selector: '[pr-employment-access]',
  standalone: true,
})
export class EmploymentAccessDirective implements OnInit {
  @Input('pr-employment-access') set setEmploymentId(employmentIds: number | string | number[] | string[]) {
    this.employmentIds = map(castArray(employmentIds), toNumber);
    this.viewRef = null;
    this.updateView();
  }

  @Input('pr-employment-accessPermissions') set setPermissions(permission: string | string[]) {
    this.permissions = castArray<string>(permission);
    this.viewRef = null;
    this.updateView();
  }

  @Input('pr-employment-accessStrict') set setStrict(strict: boolean) {
    this.strict = strict;
    this.viewRef = null;
    this.updateView();
  }

  @Input('pr-employment-accessCachePerRoute') set setCachePerRoute(cachePerRoute: boolean) {
    this.cachePerRoute = cachePerRoute;
    this.viewRef = null;
    this.clearViewContainer();
    this.updateView();
  }

  @Input('pr-employment-accessEnabled') set setEnabled(enabled: boolean) {
    this.enabled = enabled;
    this.updateView();
  }

  @Input('app-permissionElse') set setElseTemplateRef(templateRef: TemplateRef<any>) {
    this.elseTemplateRef = templateRef;
    this.elseViewRef = null;
    this.updateView();
  }

  @Input('pr-employment-accessEmployeeId') set setEmployeeId(employeeId: string) {
    this.employeeId = employeeId;
    this.updateView();
  }

  private readonly destroyRef = inject(DestroyRef);
  private cachePerRoute = true;
  private elseTemplateRef: TemplateRef<any> | null = null;
  private elseViewRef: EmbeddedViewRef<any> | null = null;
  private employeeId: string;
  private employmentIds: number[];
  private enabled = true;
  private permissions: string[] = [];
  private strict = false;
  private viewRef: EmbeddedViewRef<any> | null = null;

  constructor(
    private templateRef: TemplateRef<any>,
    private viewContainer: ViewContainerRef,
    private employmentAccessService: EmploymentAccessService,
    private employmentAccessDataFetcherService: EmploymentAccessDataFetcherService,
    private loggedUserService: LoggedUserService
  ) {}

  ngOnInit() {
    this.getEmploymentAccessData()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.updateView());
  }

  private getEmploymentAccessData(): Observable<EmploymentAccess[]> {
    return this.employmentAccessDataFetcherService.managedEmploymentsAccess$;
  }

  private getPermissions(): EmploymentAccessPermission[] {
    return this.permissions as EmploymentAccessPermission[];
  }

  private clearViewContainer() {
    this.viewContainer.clear();
  }

  private loadView() {
    this.elseViewRef = null;
    this.viewRef = this.viewContainer.createEmbeddedView(this.templateRef);
  }

  private loadElseView() {
    this.viewRef = null;
    this.elseViewRef = this.viewContainer.createEmbeddedView(this.elseTemplateRef);
  }

  private updateView() {
    this.clearViewContainer();

    if (this.loggedUserService.isLoggedUserButNotSystemOwner(this.employeeId)) {
      return;
    }

    if (!this.enabled) {
      this.loadView();

      return;
    }

    if (!this.employmentIds?.length) {
      return;
    }

    merge([this.employmentAccessDataFetcherService.managedEmploymentsAccess$, this.employmentAccessDataFetcherService.myEmploymentsAccess$])
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const hasPermissions: boolean = some(this.employmentIds, (employmentId: number) =>
          this.employmentAccessService.isAccessGranted(employmentId, this.getPermissions(), this.strict)
        );

        if (hasPermissions) {
          this.loadView();
        } else if (this.elseTemplateRef) {
          this.loadElseView();
        }
      });
  }
}
